import React, { useMemo } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import cx from 'classnames'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { l } from '@manychat/manyui'

import { BtnV2WithKeyCode } from 'apps/aiTextImprover/components/BtnV2WithKeyCode/BtnV2WithKeyCode'
import { StepLayout } from 'apps/aiTextImprover/components/StepLayout/StepLayout'
import { Textarea } from 'apps/aiTextImprover/components/Textarea/Textarea'
import {
  TextImproverValidationMessages,
  TextImproverValidationRules,
} from 'apps/aiTextImprover/lib/validation'
import { isAnyHelperKeyPressed } from 'utils/dom'

import cm from './WriteTextStep.module.css'

interface WriteTextStepProps {
  text: string
  onSubmit: (text: string) => void
  maxLength: number
}

export const WriteTextStep = ({ text, onSubmit, maxLength }: WriteTextStepProps) => {
  const validationMessage = TextImproverValidationMessages.getMinMaxValidationMessage(
    TextImproverValidationRules.textToImprove.min,
    maxLength,
  )

  const resolver = useMemo(
    () =>
      zodResolver(
        z.object({
          text: z
            .string()
            .trim()
            .min(TextImproverValidationRules.textToImprove.min, validationMessage)
            .max(maxLength, validationMessage),
        }),
      ),
    [maxLength, validationMessage],
  )

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      text,
    },
    resolver,
  })

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !isAnyHelperKeyPressed(event)) {
      event.preventDefault()
      handleSubmit(submitText)()
    }
  }

  const submitText = (values: { text: string }) => {
    onSubmit(values.text)
  }

  return (
    <StepLayout
      header={
        <h1 className="visually-hidden">{l.translate('Write a text that you want to improve')}</h1>
      }
      visuallyEmptyHeader
      footer={
        <BtnV2WithKeyCode
          data-test-id="ai-text-improver/submit-source-text"
          fullWidth
          onClick={handleSubmit(submitText)}
        >
          {l.translate('Improve')}
        </BtnV2WithKeyCode>
      }
    >
      <div className={cx(cm.content, !isValid && cm.content_withError)}>
        <Textarea
          {...register('text')}
          placeholder={l.translate('Enter text to improve...')}
          autoFocus
          aria-invalid={!isValid}
          aria-describedby={!isValid ? 'text-to-improve-error' : undefined}
          onKeyDown={onKeyDown}
          data-test-id="ai-text-improver/enter-source-text-input"
        />

        {errors.text?.message && (
          <p id="text-to-improve-error" className="text-danger text-sm">
            {errors.text.message}
          </p>
        )}
      </div>
    </StepLayout>
  )
}
